import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { money } from "@/lib/currency";
import { Plus, Pencil, Trash2, Loader2, X, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { AddonEditor } from "@/components/admin/AddonEditor";
import { VariantEditor } from "@/components/admin/VariantEditor";

const numPrices = (list) => list.map((a) => ({ ...a, price: parseFloat(a.price) || 0 }));
const strPrices = (list = []) => list.map((a) => ({ ...a, price: String(a.price) }));

const DIETARY = ["Vegan", "Vegetarian", "Gluten-Free", "Spicy", "Chef Special"];
const empty = { name: "", description: "", price: "", category: "General", image_path: null, dietary: [], available: true, addons: [], variant_groups: [] };

export const MenuManager = ({ currency = "$", staffMode = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {item}
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/my/menu");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(empty);
    setModal({ mode: "new" });
  };
  const openEdit = (item) => {
    setForm({ ...item, price: String(item.price), addons: strPrices(item.addons), variant_groups: (item.variant_groups || []).map((g) => ({ ...g, options: strPrices(g.options) })) });
    setModal({ mode: "edit", id: item.id });
  };

  const toggleDiet = (d) =>
    setForm((f) => ({ ...f, dietary: f.dietary.includes(d) ? f.dietary.filter((x) => x !== d) : [...f.dietary, d] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, addons: numPrices(form.addons), variant_groups: form.variant_groups.map((g) => ({ ...g, options: numPrices(g.options) })) };
      if (modal.mode === "new") await api.post("/my/menu", payload);
      else await api.put(`/my/menu/${modal.id}`, payload);
      toast.success("Menu saved");
      setModal(null);
      load();
    } catch {
      toast.error("Could not save item");
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailable = async (it) => {
    await api.patch(`/my/menu/${it.id}/availability`, { available: !it.available });
    toast.success(`${it.name} marked ${it.available ? "sold out" : "available"}`);
    load();
  };

  const toggleVariant = async (it, o) => {
    const on = o.available === false;
    await api.patch(`/my/menu/${it.id}/variants/${o.id}/availability`, { available: on });
    toast.success(`${it.name} (${o.name}) marked ${on ? "available" : "sold out"}`);
    load();
  };

  const toggleAddon = async (it, a) => {
    const on = a.available === false;
    await api.patch(`/my/menu/${it.id}/addons/${a.id}/availability`, { available: on });
    toast.success(`${a.name} marked ${on ? "available" : "sold out"}`);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/my/menu/${id}`);
    toast.success("Item deleted");
    load();
  };

  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-orange-500" />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold">Menu</h2>
          <p className="text-slate-400 text-sm">{items.length} items</p>
        </div>
        {!staffMode && (
          <button data-testid="add-menu-item-button" onClick={openNew} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 transition-colors rounded-xl px-4 py-2.5 text-sm font-semibold">
            <Plus className="h-4 w-4" /> Add item
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-slate-700 rounded-2xl p-12 text-center text-slate-400">
          No items yet. Add your first dish to get started.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`menu-item-card-${it.id}`} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
              <div className={`${it.image_path ? "h-36" : "h-14 sm:h-36"} bg-slate-800`}>
                {it.image_path ? (
                  <img src={fileUrl(it.image_path)} alt={it.name} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-slate-400 text-sm bg-slate-800/60">
                    <div className="flex sm:flex-col items-center gap-2 sm:gap-1">
                      <UtensilsCrossed className="h-6 w-6 text-slate-500" />
                      <span className="text-xs">No image</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-sub font-semibold">{it.name}</h3>
                    <p className="text-xs text-slate-400">{it.category}</p>
                  </div>
                  <span className="font-mono-j text-orange-400 font-bold">{it.variant_groups?.length ? "from " : ""}{money(it.price, currency)}</span>
                </div>
                {it.description && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{it.description}</p>}
                <div className="flex items-center gap-2 mt-3">
                  {!it.available && <span data-testid={`sold-out-badge-${it.id}`} className="text-xs bg-red-950/60 text-red-400 px-2 py-0.5 rounded-full">Sold out</span>}
                  {it.dietary?.slice(0, 2).map((d) => (
                    <span key={d} className="text-xs bg-emerald-950/50 text-emerald-400 px-2 py-0.5 rounded-full">{d}</span>
                  ))}
                </div>
                <button data-testid={`toggle-available-${it.id}`} onClick={() => toggleAvailable(it)} className="mt-4 w-full flex items-center justify-between bg-slate-800/60 hover:bg-slate-800 rounded-lg px-3 py-2 text-sm transition-colors">
                  <span className={it.available ? "text-emerald-400" : "text-red-400"}>{it.available ? "Available" : "Sold out"}</span>
                  <span className={`relative h-5 w-9 rounded-full transition-colors ${it.available ? "bg-emerald-600" : "bg-slate-600"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${it.available ? "translate-x-4" : "translate-x-0.5"}`} />
                  </span>
                </button>
                {it.variant_groups?.map((g, gi) => (
                  <div key={g.id} className="mt-2 space-y-1" data-testid={`variant-toggles-${g.id}`}>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500">{g.name}</p>
                    {g.options.map((o) => (
                      <button key={o.id} data-testid={`toggle-variant-${o.id}`} onClick={() => toggleVariant(it, o)} className="w-full flex items-center justify-between gap-2 bg-slate-800/40 hover:bg-slate-800 rounded-lg px-3 py-1.5 text-xs transition-colors">
                        <span className="truncate text-left">{o.name} <span className="text-slate-500 font-mono-j">{gi === 0 ? money(o.price, currency) : o.price ? `+${money(o.price, currency)}` : ""}</span></span>
                        <span className={o.available === false ? "text-red-400" : "text-emerald-400"}>{o.available === false ? "Sold out" : "Available"}</span>
                      </button>
                    ))}
                  </div>
                ))}
                {it.addons?.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid={`addon-toggles-${it.id}`}>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500">Add-ons</p>
                    {it.addons.map((a) => (
                      <button key={a.id} data-testid={`toggle-addon-${a.id}`} onClick={() => toggleAddon(it, a)} className="w-full flex items-center justify-between gap-2 bg-slate-800/40 hover:bg-slate-800 rounded-lg px-3 py-1.5 text-xs transition-colors">
                        <span className="truncate text-left">{a.name} <span className="text-slate-500 font-mono-j">+{money(a.price, currency)}</span></span>
                        <span className={a.available === false ? "text-red-400" : "text-emerald-400"}>{a.available === false ? "Sold out" : "Available"}</span>
                      </button>
                    ))}
                  </div>
                )}
                {!staffMode && <div className="flex gap-2 mt-2">
                  <button data-testid={`edit-item-${it.id}`} onClick={() => openEdit(it)} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg py-2 text-sm transition-colors">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button data-testid={`delete-item-${it.id}`} onClick={() => remove(it.id)} className="inline-flex items-center justify-center bg-red-950/50 hover:bg-red-900/60 text-red-400 rounded-lg py-2 px-3 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold">{modal.mode === "new" ? "Add item" : "Edit item"}</h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <ImageUpload value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} label="Dish photo" testid="menu-image-upload" />
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Name</label>
                <input data-testid="item-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className={form.variant_groups?.length ? "hidden" : ""}>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Price ({currency || "$"})</label>
                  <input data-testid="item-price-input" required={!form.variant_groups?.length} type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Category</label>
                  <input data-testid="item-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Description</label>
                <textarea data-testid="item-description-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500 resize-none" />
              </div>
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Dietary tags</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DIETARY.map((d) => (
                    <button type="button" key={d} data-testid={`diet-tag-${d}`} onClick={() => toggleDiet(d)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.dietary.includes(d) ? "bg-orange-600 border-orange-600 text-white" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <VariantEditor groups={form.variant_groups || []} onChange={(variant_groups) => setForm({ ...form, variant_groups })} currency={currency} />
              <AddonEditor addons={form.addons || []} onChange={(addons) => setForm({ ...form, addons })} currency={currency} />
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="item-available-toggle" type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} className="h-4 w-4 accent-orange-600" />
                <span className="text-sm">Available to customers</span>
              </label>
              <button data-testid="save-item-button" type="submit" disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition-colors">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save item
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
